import React, { useEffect, useMemo } from 'react';
import { Field, InfoTip } from '../shared/FormKit';
import ReviewerSelect, { useReviewerOptions } from './ReviewerSelect';
import {
  applyAutoAnswers, qcReviewerPlanOf, REVIEWER_STAGE_FIELDS, NAS_TYPE_OPTIONS,
} from '../../pages/qcQuestions';

// The reviewer pickers of the QC6/QC7 form flows (QC6 / QC7 Forms, QC Form
// Amendment, QC6/QC7 Addendum) — driven by the form's REVIEWER PLAN
// (qcQuestions.qcReviewerPlanOf): the engagement type, the non-assurance
// service type, whether the client is a PIE (Director route) and, for a QC7,
// whether the risk rating moved from prior year (Partner route) decide how many
// reviewers the request names and of which grade. One picker per stage, each
// listing only the grade that stage calls for, with a hover note saying which
// answer put it there; the same person cannot fill two stages. Used by the New page, the Modify modal and
// the Addendum surfaces alike, so the three read the plan the same way.
//
//   picks    { reviewerStaffId, additionalReviewerStaffId, thirdReviewerStaffId }
//            (strings; '' = none)
//   onChange(nextPicks)
//   plan     qcReviewerPlanOf(...) — see planFor / useQcReviewerPlan
//   hint     the first picker's hint (who-picks wording per flow)

const ORDINAL = ['first', 'second', 'third'];
const GRADE = { partner: 'Partner', director: 'Director' };
const SERVICE = { audit: 'Audit / review engagement', nas: 'Non-assurance engagement' };
const NAS = { routine: NAS_TYPE_OPTIONS[0], non_routine: NAS_TYPE_OPTIONS[1], fas: NAS_TYPE_OPTIONS[2] };

/** The plan a form's answers call for (auto-answers applied first). */
export const planFor = (answers, qcType, opts) =>
  qcReviewerPlanOf(applyAutoAnswers(answers || {}, qcType), qcType, opts);

export const useQcReviewerPlan = (answers, qcType, opts) =>
  useMemo(() => planFor(answers, qcType, opts), [answers, qcType, opts]);

/** Empty picks — the shape every surface keeps in state. */
export const emptyReviewerPicks = () => ({ reviewerStaffId: '', additionalReviewerStaffId: '', thirdReviewerStaffId: '' });

/** Picks read off a saved request / draft (ids as strings). */
export const reviewerPicksOf = (request) => {
  const out = emptyReviewerPicks();
  REVIEWER_STAGE_FIELDS.forEach((f) => { out[f] = request && request[f] ? String(request[f]) : ''; });
  return out;
};

/**
 * The still-empty pickers, as `[{ key, label }]` for formValidation — keyed
 * to the `field=` of the picker (the reviewer NAME field, the same key the
 * return-comment anchors and History use). No plan yet ⇒ one gap naming the
 * form questions that decide it.
 */
export const reviewerPickGaps = (plan, picks) => {
  if (!plan || !plan.stages) {
    return [{ key: 'reviewerName', label: `Reviewers — answer "${(plan ? plan.missing : []).join('" and "')}" in the form first` }];
  }
  return plan.stages
    .filter((s) => !(picks && picks[s.field]))
    .map((s) => ({ key: s.nameField, label: `${s.label} (${GRADE[s.grade]})` }));
};

/** Put the planned picks on a FormData (stages the plan does not use are left out — the server clears them). */
export const appendReviewerPicks = (fd, plan, picks) => {
  (plan && plan.stages ? plan.stages : []).forEach((s) => {
    if (picks && picks[s.field]) fd.set(s.field, String(picks[s.field]));
  });
  return fd;
};

/** One line saying which route the answers chose and who that means. */
export const planSummary = (plan, qcType) => {
  if (!plan || !plan.stages) return null;
  const directorRoute = plan.serviceType === 'nas' && (plan.nasType === 'routine' || plan.nasType === 'non_routine');
  const fact = directorRoute
    ? (plan.pie ? 'PIE client' : 'not a PIE')
    : (String(qcType).toUpperCase() === 'QC7' && plan.serviceType === 'audit'
      ? (plan.riskChanged ? 'risk rating changed from prior year' : 'risk rating unchanged from prior year')
      : null);
  const route = [SERVICE[plan.serviceType], plan.nasType ? NAS[plan.nasType] : null, fact]
    .filter(Boolean).join(' · ');
  const who = plan.stages.map((s) => GRADE[s.grade]).join(' → ');
  return `${route} → ${who}`;
};

/** Why this stage exists and why its list is Partners or Directors — the picker's hover note. */
export const stageReason = (plan, stage, index, qcType) => {
  const directorRoute = plan.serviceType === 'nas' && (plan.nasType === 'routine' || plan.nasType === 'non_routine');
  const chosen = [SERVICE[plan.serviceType], plan.nasType ? NAS[plan.nasType] : null].filter(Boolean).map((t) => `"${t}"`).join(' and ');
  const grade = GRADE[stage.grade];
  const isQc7 = String(qcType || '').toUpperCase() === 'QC7';
  if (index === 0) {
    return directorRoute
      ? `Because you chose ${chosen}, the first reviewer must be a Director — only Directors are listed.`
      : `Because you chose ${chosen}, the first reviewer must be a Partner — only Partners are listed.`;
  }
  if (index === 1) {
    if (!isQc7) return `A QC6 always needs a second approval; because you chose ${chosen}, it must be a ${grade} — only ${grade}s are listed.`;
    if (directorRoute) return `Because you chose ${chosen}, a Partner approves after the Director — only Partners are listed.`;
    if (plan.nasType === 'fas') return `Because you chose ${chosen}, two Partners must approve — only Partners are listed.`;
    return 'Because "Client risk rated in current year" differs from "Risk rating in prior year" (the risk rating changed), a second Partner must approve — only Partners are listed.';
  }
  return 'Because you answered Yes to "Public Interest Entity (PIE)?", a third reviewer (Partner) must approve — only Partners are listed.';
};

const QcReviewerPicks = ({ plan, picks, onChange, hint = 'You pick who reviews this request, from the firm\'s roster.', qcType = null }) => {
  const roster = useReviewerOptions();
  const roleOf = useMemo(() => new Map(roster.map((r) => [String(r.staffId), r.role])), [roster]);

  // A pick that no longer fits its stage (the answers changed the plan — a
  // Partner sits in a stage that now wants a Director, or the stage went
  // away) or is no longer on the roster (left the firm) is dropped, so the
  // form asks for a new one instead of submitting a pick the server refuses.
  useEffect(() => {
    if (!plan || !plan.stages || roster.length === 0 || !picks) return;
    const next = { ...picks };
    let changed = false;
    REVIEWER_STAGE_FIELDS.forEach((f) => {
      const stage = plan.stages.find((s) => s.field === f);
      const v = picks[f];
      if (!v) return;
      if (!stage || !roleOf.has(String(v)) || roleOf.get(String(v)) !== stage.grade) { next[f] = ''; changed = true; }
    });
    if (changed) onChange(next);
  }, [plan, roster, roleOf, picks, onChange]);

  if (!plan || !plan.stages) {
    return (
      <p className="text-sm text-slate-500">
        Answer &ldquo;{(plan ? plan.missing : []).join('” and “')}&rdquo; in the form above — the reviewers depend on it.
      </p>
    );
  }
  const summary = planSummary(plan, qcType);
  return (
    <>
      {summary && <p className="text-xs text-slate-500 -mt-1">{summary}</p>}
      {plan.stages.map((s, i) => (
        <Field
          key={s.key}
          label={(
            <>
              {plan.stages.length > 1 ? s.label : 'Reviewer'} ({GRADE[s.grade]})
              {' '}<InfoTip text={stageReason(plan, s, i, qcType)} />
            </>
          )}
          required
          field={s.nameField}
          hint={i === 0 ? hint : `Approves after the ${ORDINAL[i - 1]} reviewer.`}
        >
          <ReviewerSelect
            grade={s.grade}
            value={picks[s.field] || ''}
            onChange={(v) => onChange({ ...picks, [s.field]: v })}
            excludeIds={REVIEWER_STAGE_FIELDS.filter((f) => f !== s.field).map((f) => picks[f]).filter(Boolean)}
          />
        </Field>
      ))}
    </>
  );
};

export default QcReviewerPicks;
